import { ApiFilter } from "@result/rui/utils/api";

/**
 * Data layer filter
 *
 */
export default class DataLayerFilter {
  #api = null;

  #func = null;

  /**
   * Constructor for the DataLayerFilter class.
   *
   * @param {ApiFilter} api - API instance to use for fetching data
   * @param {function} fn - Optional function to apply additional client-side filtering to the results from the service. It should take an item and return true if it should be included in the results, or false if it should be excluded.
   */
  constructor(api, fn) {
    this.#api = new ApiFilter().from(api);
    this.#func = fn ?? convertToFunc(this.#api);
  }

  get api() {
    return this.#api;
  }

  get func() {
    return this.#func;
  }
}

/**
 * Extract the API filter from param
 *
 * @param {DataLayerFilter|ApiFilter} f
 */
export function apiFilter(f) {
  if (typeof f === "function") {
    // eslint-disable-next-line no-param-reassign
    f = f();
  }

  if (f instanceof DataLayerFilter) {
    return f.api;
  }

  if (f instanceof ApiFilter) {
    return f;
  }

  if (f && typeof f === "object" && typeof f === "string") {
    return new ApiFilter().from(f); // validate the filter object
  }

  if (f === null || f === undefined) {
    return null;
  }

  // eslint-disable-next-line no-console
  console.error("Invalid filter provided to apiFilter:", f);
  return null;
}

export function funcFilter(f) {
  if (!f) return null;

  if (f instanceof DataLayerFilter) {
    return f.func;
  }

  if (typeof f === "function") {
    // assume we already have the filter function, just return it
    return f;
  }

  if (f instanceof ApiFilter) {
    return convertToFunc(f);
  }
  // eslint-disable-next-line no-console
  console.error("Invalid filter function provided to funcFilter:", f);
  return null;
}

/**
 * Convers an ApiFilter into a function that can be applied to an array of items to filter them client-side.
 *
 * @param {ApiFilter} filter
 */

export function convertToFunc(filter) {
  const unary = new Set(["ISNULL", "ISNOTNULL", "ISFALSE", "ISTRUE"]);
  const caseInsensitive = new Set([
    "EQ_IC",
    "NEQ_IC",
    "LIKE_IC",
    "IN_IC",
    "NIN_IC",
  ]);
  const isNumericString = (value) =>
    typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim());

  const normalizeComparable = (value) => {
    if (value instanceof Date) return value.toISOString();
    if (isNumericString(value)) return Number(value);

    return value;
  };

  const normalizeForEquality = (left, right, operator) => {
    const normalizedLeft = normalizeComparable(left);
    const normalizedRight = normalizeComparable(right);

    if (
      caseInsensitive.has(operator) &&
      typeof normalizedLeft === "string" &&
      typeof normalizedRight === "string"
    ) {
      return [normalizedLeft.toLowerCase(), normalizedRight.toLowerCase()];
    }

    return [normalizedLeft, normalizedRight];
  };

  const parseListValue = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return [value];

    const trimmed = value.trim();
    if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) return [value];

    const inner = trimmed.slice(1, -1).trim();
    if (!inner) return [];

    return inner
      .split(",")
      .map((entry) => entry.trim())
      .filter((entry) => entry !== "");
  };

  const getValueAtPath = (source, path) => {
    if (!source || !path || typeof path !== "string") return undefined;
    return path.split(".").reduce((current, part) => {
      if (current == null) return undefined;
      return current[part];
    }, source);
  };

  const escapeRegex = (value) =>
    String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const like = (leftValue, rightValue, insensitive = false) => {
    if (leftValue == null || rightValue == null) return false;

    const pattern = String(rightValue);

    // The BE treats LIKE values without explicit wildcards as "contains"
    // (implicit %value%) — mirror that here so offline filtering behaves
    // the same as online (e.g. FilterTextInput emits bare LIKE_IC values).
    const hasWildcards = /[%_]/.test(pattern);

    const regexSource = escapeRegex(pattern)
      .replace(/%/g, ".*")
      .replace(/_/g, ".");
    const regex = new RegExp(
      hasWildcards ? `^${regexSource}$` : regexSource,
      insensitive ? "i" : ""
    );

    return regex.test(String(leftValue));
  };

  const evaluateNode = (node, item) => {
    if (!node || typeof node !== "object") return false;

    if (node.logical && Array.isArray(node.fields)) {
      if (node.logical === "OR") {
        return node.fields.some((child) => evaluateNode(child, item));
      }
      return node.fields.every((child) => evaluateNode(child, item));
    }

    const name = node.name ?? node.field;
    const operator = String(node.operator ?? "").toUpperCase();

    if (!name || (!operator && !unary.has(operator))) return false;

    const itemValue = getValueAtPath(item, name);

    // considering undefined as null for ISNULL/ISNOTNULL checks,
    // as that's often more intuitive in JS context
    if (operator === "ISNULL")
      return itemValue === null || itemValue === undefined;
    if (operator === "ISNOTNULL")
      return !(itemValue === null || itemValue === undefined);

    if (operator === "ISFALSE") return itemValue === false;
    if (operator === "ISTRUE") return itemValue === true;

    if (!operator) return false;

    const { value } = node;

    if (operator === "LIKE") return like(itemValue, value, false);
    if (operator === "LIKE_IC") return like(itemValue, value, true);

    if (
      operator === "IN" ||
      operator === "IN_IC" ||
      operator === "NIN" ||
      operator === "NIN_IC"
    ) {
      const values = parseListValue(value);
      const includes = values.some((candidate) => {
        const [left, right] = normalizeForEquality(
          itemValue,
          candidate,
          operator.includes("_IC") ? operator : "EQ"
        );
        return left === right;
      });

      return operator === "IN" || operator === "IN_IC" ? includes : !includes;
    }

    if (["EQ", "EQ_IC", "NEQ", "NEQ_IC"].includes(operator)) {
      const [left, right] = normalizeForEquality(itemValue, value, operator);
      const equal = left === right;

      return operator.startsWith("NEQ") ? !equal : equal;
    }

    if (["GT", "GTE", "LT", "LTE"].includes(operator)) {
      const left = normalizeComparable(itemValue);
      const right = normalizeComparable(value);

      if (left == null || right == null) return false;
      if (operator === "GT") return left > right;
      if (operator === "GTE") return left >= right;
      if (operator === "LT") return left < right;
      return left <= right;
    }

    return false;
  };

  const resolveFilterNodes = () => {
    if (filter == null) return null;
    if (typeof filter === "string") {
      try {
        return new ApiFilter().from(filter).toJSON();
      } catch (_error) {
        return null;
      }
    }
    if (typeof filter?.toJSON === "function") return filter.toJSON();
    if (Array.isArray(filter)) return filter;
    if (typeof filter === "object") return filter;
    return null;
  };

  const resolved = resolveFilterNodes();
  if (resolved == null) return () => false;

  const rootNodes = Array.isArray(resolved) ? resolved : [resolved];
  if (rootNodes.length === 0) return () => true;

  return (item) => rootNodes.every((node) => evaluateNode(node, item));
}
